import { ChargingContractResource, ChargingContractResourceType, GetContractResponse } from '@/api/generated/engageUIApiEndpoints.schemas'
import { ContractStatus } from '@/pages/ContractSearchPage/types'
import { Contract, ContractType } from '@/domain/contract/types'
import { ContractCreatorType, ContractTariffBooking, ContractTariffBookings, SalesPartnerName, Salutation } from '@/domain/contract/types/contract'

export const mapToContract = (resource: GetContractResponse): Contract => {
  // TODO mtirpitz: remove fallback once the contract number is available for all contracts
  const contractNumber = resource.contract.contractNumber ?? `V-${resource.contract.id}`

  return {
    header: {
      contractNumber,
      contractType: determineContractType(resource.contract.type),
      contractStatus: determineContractStatus(resource.contract.termination),
      billingAccountId: resource.contract.billing.billingAccountId,
      businessPartnerIdExternal: undefined,
      myEnergyKeySubject: resource.identity?.myEnergyKey.subject,
      legacyEndUserId: resource.contract.legacyEndUserId
    },
    masterData: {
      keyFigures: {
        emaId: resource.contract.chargingAccount.emaId,
        billingAccountIdExternal: resource.contract.billing.accountIdExternal
      },
      contractDetails: {
        contractNumber,
        contractType: determineContractType(resource.contract.type),
        createdAt: new Date(resource.contract.creation.createdAt),
        createdByType: ContractCreatorType[resource.contract.creation.createdBy.type]
      },
      contractPartner: {
        salutation: Salutation[resource.contract.personalData.salutation],
        firstName: resource.contract.personalData.firstName,
        lastName: resource.contract.personalData.lastName,
        // TODO mtirpitz: update TypeScript configuration and replace "Date" with "Temporal.PlainDate"
        birthday: resource.contract.personalData.birthday ? new Date(Date.parse(resource.contract.personalData.birthday)) : undefined,
        email: resource.contract.communication.emailAddress,
        phoneNumber: resource.contract.communication.phoneNumber,
        alternativePhoneNumber: resource.contract.communication.alternativePhoneNumber
      },
      billingAddress: {
        street: resource.contract.address.street,
        houseNumber: resource.contract.address.houseNumber,
        postalCode: resource.contract.address.postalCode,
        city: resource.contract.address.city,
        country: resource.contract.address.countryCode
      }
    },
    tariffBookings: mapTariffBookings(resource)
  }
}

const mapTariffBookings = (resource: GetContractResponse): ContractTariffBookings => {
  const sortedTariffBookingResources = [...resource.contract.bookings].sort(
    (booking, otherBooking) => new Date(booking.validFrom).getTime() - new Date(otherBooking.validFrom).getTime()
  )
  const tariffBookings = sortedTariffBookingResources.map((booking, index) => {
    const nextBooking = sortedTariffBookingResources[index + 1]
    let validUntil: Date | undefined

    if (nextBooking) {
      validUntil = new Date(nextBooking.validFrom)
    } else if (resource.contract.termination?.terminateAt) {
      validUntil = new Date(resource.contract.termination.terminateAt)
    }

    return {
      tariffId: booking.tariffId,
      tariffName: booking.tariffName,
      validFrom: new Date(booking.validFrom),
      validUntil,
      bookedAt: new Date(booking.bookedAt),
      salesPartnerName: SalesPartnerName[booking.bookingSalesPartner?.salesPartnerReference.salesPartnerName as keyof typeof SalesPartnerName],
      salesPartnerCustomerReferenceId: booking.bookingSalesPartner?.salesPartnerCustomerReferenceId
    }
  })

  const pastTariffBookings = tariffBookings
    .filter((tariffBooking) => tariffBooking.validUntil !== undefined && tariffBooking.validUntil.getTime() <= Date.now())
    .reverse()

  const currentTariffBooking: ContractTariffBooking | undefined = tariffBookings.find(
    (tariffBooking) =>
      tariffBooking.validFrom.getTime() <= Date.now() && (tariffBooking.validUntil === undefined || tariffBooking.validUntil.getTime() > Date.now())
  )

  const futureTariffBooking: ContractTariffBooking | undefined = tariffBookings.find((tariffBooking) => tariffBooking.validFrom.getTime() > Date.now())

  return {
    pastTariffBookings,
    currentTariffBooking,
    futureTariffBooking
  }
}

const determineContractStatus = (termination: ChargingContractResource['termination']): ContractStatus => {
  if (!termination?.terminateAt) {
    return ContractStatus.Active
  }

  const now = new Date()
  const terminationDate = new Date(termination.terminateAt)
  if (now >= terminationDate) {
    return ContractStatus.Terminated
  }

  return ContractStatus.InTermination
}

const determineContractType = (type: ChargingContractResourceType): ContractType => {
  switch (type) {
    case ChargingContractResourceType.PRIVATE:
      return ContractType.B2C
    case ChargingContractResourceType.COMPANY:
    case ChargingContractResourceType.PUBLIC:
    case ChargingContractResourceType.FLEET:
      return ContractType.B2B
    case ChargingContractResourceType.UNSPECIFIED:
      return ContractType.UNSPECIFIED
  }
}
